// Personnel cost cascade — données réelles agrégées par cost pool TDABC.
//
// Backend : services/tdabc/personnel_cost_cascade_service.py
// Route    : GET /api/tdabc/personnel-cost-cascade/{tenant_id}?year=YYYY
//
// Le payload retourne :
//   { is_mock, year, pools: [{pool_id, pool_name, is_unassigned, personnel,
//     absenteism, corporate, supervision, production_hours}, …], totals: {…} }
//
// Mapping HrPerson (source CHR yearly) ↔ TdabcPerson (porte cost_pool_id) se
// fait par nom complet normalisé côté backend — TODO : remplacer par une FK
// explicite `TdabcPerson.hr_person_id` (cf. service backend).

import api from '../client';

export function fetchPersonnelCostCascade(tenantId, year) {
  if (!tenantId || !year) {
    return Promise.resolve({
      is_mock: true,
      year:    year || new Date().getFullYear(),
      pools:   [],
      totals:  { personnel: 0, absenteism: 0, corporate: 0, supervision: 0, production_hours: 0 },
    });
  }
  return api
    .get(`/tdabc/personnel-cost-cascade/${tenantId}`, { params: { year } })
    .then(r => r.data?.data || r.data);
}

// ── Persistance localStorage du choix d'allocation par tenant ────────────────
// Clé : `cost_pool_cascade_mode_{tenant_id}` → JSON {absenteism, corporate, supervision}
// Phase B+ : déplacer la préférence côté backend (table dédiée ou champ tenant).

// Clés d'allocation FIXES (plus de choix utilisateur) :
//   - 'within'   : spécifique au groupe (coût gardé dans son groupe)
//   - 'shared'   : capacité pratique (coût mutualisé sur Σ TR)
//   - 'excluded' : exclu du périmètre analytique (0 réaffectation)
// Règle : congés spéciaux exclus · gestion entreprise sur capacité pratique ·
// supervision production spécifique au groupe · frais & charges indirectes sur
// capacité pratique.
const DEFAULT_CASCADE_MODE = {
  absenteism:     'excluded',  // congés spéciaux → hors périmètre analytique
  corporate:      'shared',    // gestion entreprise → capacité pratique (Σ TR)
  supervision:    'within',    // supervision production → spécifique au groupe
  expenses:       'shared',
  indirect_costs: 'shared',
};

// Plus de choix d'inducteur : tout est alloué sur la capacité pratique (Σ TR).
// Le toggle a été retiré ; on ignore tout mode éventuellement stocké et on
// renvoie toujours le profil par défaut (100 % capacité pratique). Signature
// conservée (tenantId) : plusieurs pages appellent readCascadeMode(tenantId).
export function readCascadeMode() {
  return { ...DEFAULT_CASCADE_MODE };
}

export function writeCascadeMode(tenantId, mode) {
  if (!tenantId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`cost_pool_cascade_mode_${tenantId}`, JSON.stringify(mode));
  } catch (_e) { /* quota / privé : on ignore silencieusement en Phase A */ }
}

// Helper : pour un composant donné et un mode, retourne le coût alloué au pool.
// Réplique la logique de CostAssignmentPage.alloc() côté lib réutilisable.
function _allocCost(component, mode, pool, totals) {
  if (mode === 'within') return pool[component] || 0;
  if (mode === 'excluded') return 0;   // exclure des affectations : 0 réaffectation
  const totalAmt = totals[component] || 0;
  // shared (Production hours) — seul mode global restant
  const tHrs = totals.production_hours || 0;
  return tHrs > 0 ? (totalAmt / tHrs) * (pool.production_hours || 0) : 0;
}

/**
 * Calcule le burden rate horaire (CHF/h) par pool depuis la cascade et le mode persisté.
 * = (Absenteism + Corporate + Supervision + Σ Externes net) / Production hours du pool.
 * Σ Externes net inclut : Expenses (mode expenses), Indirect costs résiduel (mode indirect),
 * Specific indirect cost (saisi direct) − Indemnités reçues (mode absenteism, miroir).
 * Indirect costs résiduel = total indirect_costs − Σ specific_indirect (jamais 'within').
 *
 * @returns {Object<string, number>} map { pool_id → burden_rate (CHF/h) }
 */
export function computeBurdenRatesByPool(cascade, mode) {
  const result = {};
  if (!cascade?.pools?.length) return result;
  const t = cascade.totals || {};
  const totalSpecific = t.specific_indirect || 0;
  const totalCIGross  = t.indirect_costs    || 0;
  const totalCIResid  = Math.max(0, totalCIGross - totalSpecific);
  // Total cascade adapté pour l'allocation de CI residual (ne contient pas specific)
  const cascadeResid  = { ...t, indirect_costs: totalCIResid };

  for (const p of cascade.pools) {
    const corp      = _allocCost('corporate',            mode.corporate,      p, t);
    const sup       = _allocCost('supervision',          mode.supervision,    p, t);
    const exp       = _allocCost('expenses',             mode.expenses,       p, t);
    const specInd   = p.specific_indirect || 0;
    const indirectResid = _allocCost('indirect_costs', 'shared', p, cascadeResid);

    // Congés spéciaux exclus du périmètre analytique ; indemnités perçues ignorées.
    const indirectTotal = corp + sup + exp + specInd + indirectResid;
    const tr = p.production_hours || 0;
    result[p.pool_id] = tr > 0 ? indirectTotal / tr : 0;
  }
  return result;
}

/**
 * Éclate le burden rate horaire (CHF/h) par pool en ses deux mark-up :
 *   - Indirect personnel = (Absenteism + Corporate + Supervision − Indemnités reçues) ÷ TR
 *   - Overhead (ovh)     = (Specific indirect + Indirect costs résiduel + Expenses) ÷ TR
 * La somme des deux est identique à computeBurdenRatesByPool (PCR inchangé) — voir
 * CostAssignmentPage « Indirect personnel mark-up » / « Overhead mark-up ».
 *
 * @returns {Object<string, {indirect_personnel:number, overhead:number}>} map par pool_id
 */
export function computeMarkupRatesByPool(cascade, mode) {
  const result = {};
  if (!cascade?.pools?.length) return result;
  const t = cascade.totals || {};
  const totalSpecific = t.specific_indirect || 0;
  const totalCIGross  = t.indirect_costs    || 0;
  const totalCIResid  = Math.max(0, totalCIGross - totalSpecific);
  const cascadeResid  = { ...t, indirect_costs: totalCIResid };

  for (const p of cascade.pools) {
    const corp      = _allocCost('corporate',            mode.corporate,   p, t);
    const sup       = _allocCost('supervision',          mode.supervision, p, t);
    const exp       = _allocCost('expenses',             mode.expenses,    p, t);
    const specInd   = p.specific_indirect || 0;
    const indirectResid = _allocCost('indirect_costs', 'shared', p, cascadeResid);
    const tr = p.production_hours || 0;

    // Congés spéciaux exclus du périmètre analytique ; indemnités perçues ignorées.
    const indPers = corp + sup;                              // mark-up personnel indirect (sans expenses)
    const ovh     = specInd + indirectResid + exp;           // mark-up overhead (inclut expenses)
    result[p.pool_id] = {
      indirect_personnel: tr > 0 ? indPers / tr : 0,
      overhead:           tr > 0 ? ovh / tr     : 0,
    };
  }
  return result;
}

// Helper de calcul des taux distribués par pool selon le mode.
// - 'within' (Pool specific)     : cost_pool ÷ production_hours_pool
// - 'shared' (Production hours)  : Σcost ÷ Σ production_hours (taux global TR)
// - 'billed' (Billed hours)      : Σcost ÷ Σ billed_hours (taux global TF)
export function computeDistributedRate(component, mode, pool, totals) {
  const cost  = pool[component] || 0;
  if (mode === 'within') {
    const hrs = pool.production_hours || 0;
    return hrs > 0 ? cost / hrs : 0;
  }
  const totalCost = totals[component] || 0;
  if (mode === 'billed') {
    const totalHrs = totals.billed_hours || 0;
    return totalHrs > 0 ? totalCost / totalHrs : 0;
  }
  // shared (default)
  const totalHrs = totals.production_hours || 0;
  return totalHrs > 0 ? totalCost / totalHrs : 0;
}
